/* eslint-disable */
import { Map, View } from 'ol';
import { Extent, extend } from 'ol/extent';
import 'ol/ol.css';
import './Map.css';

import Feature from 'ol/Feature';
import CircleOL from 'ol/geom/Circle';
import LineString from 'ol/geom/LineString';
import LinearRing from 'ol/geom/LinearRing';
import Point from 'ol/geom/Point';
import Polygon, { fromCircle } from 'ol/geom/Polygon';
import { Vector as VectorLayer } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import { Circle, Icon, Stroke, Style } from 'ol/style';

// eslint-disable-next-line
import marker from '!file-loader!./marker.svg';
import Overlay from 'ol/Overlay';
import OverlayPositioning from 'ol/OverlayPositioning';
import GeoJSON from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import GeometryType from 'ol/geom/GeometryType';
import Draw from 'ol/interaction/Draw';
import LayerGroup from 'ol/layer/Group';
import Fill from 'ol/style/Fill';
import { createRef, useEffect, useRef, useState } from 'react';
import KoordinatSok from './KoordinatSok';

import { usePersistedState } from '../common/hooks/usePersistedState';
import ModalFelmeddelande from './ModalFelmeddelande';
import BeteckningComponent from './beteckningar/BeteckningComponent';
import PremiumContainer from './premium/PremiumContainer';
import { FeatureUtils } from './utils/FeatureUtils';
import { URLUtils } from './utils/URLUtils';

import OL3Parser from 'jsts/org/locationtech/jts/io/OL3Parser';
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp';
import KopKnappGeometrier from './KopKnappGeometrier';
import { useDataContext } from './contexts/DataContext';
import { useKartstodContext } from './contexts/KartstodContext';
import { useMapSettingsContext } from './contexts/MapSettingsContext';
import { usePremiumContext } from './contexts/PremiumContext';
import { KartaLocalStorage } from './model/KartaLocalStorage';
import { Koordinater } from './model/KartstodModel';

export const mapStateStorageKey = 'mapState';

export const mapDataStorageKey = 'data';

interface InitMapProps {
  setVisaKartStod: (ks: boolean) => void;
  getVisaKartStod: boolean;
  visaBeteckningar: boolean;
  visaRitVerktyg: boolean;
  visaKartlager: boolean;
  isPremium: boolean;
}

const InitMap = (props: InitMapProps) => {
  //Rådata
  const [json, setJson] = usePersistedState<string[]>(mapDataStorageKey, []);
  const checkLocalStorageFirstRun = useRef(true);

  //Kartan
  const mapRef = createRef<HTMLDivElement>();
  const [vectorSourceHovrad] = useState(new VectorSource());
  const [vectorSourceKopt] = useState(new VectorSource());
  const [vectorSourceMarkerad] = useState(new VectorSource());
  const [vectorSourceRitVerktyg] = useState(new VectorSource());
  const [vectorSourceBuffertZon] = useState(new VectorSource());
  const [vectorSourceMarker] = useState(new VectorSource());
  const [iconMarker, setIconMarker] = useState(new Feature<Point>());
  const [zoomLevel, setZoomLevel] = useState(false);
  const [koordinater, setKoordinater] = useState<Koordinater>({
    nKoordinat: 0,
    eKoordinat: 0,
  });
  const [map, setMap] = useState(new Map({}));

  const [beteckningar, setBeteckningar] = useState<Feature<Geometry>[]>([]);
  const [hovradBeteckning, setHovradBeteckning] = useState<Feature<Geometry>[]>(
    []
  );
  const [klickadBeteckning, setKlickadBeteckning] = useState<
    Feature<Geometry>[]
  >([]);
  const [markeradeBeteckningar, setMarkeradeBeteckningar] = useState<any>([]);

  const [isBeteckningarOpen, setIsBeteckningarOpen] = useState(false);
  const [fetchBeteckningarURL, setFetchBeteckningarURL] = useState('');

  //Diverse
  const [visaKopKnapp, setVisaKopKnapp] = useState(false);
  const [visaKopKnappGeometrier, setVisaKopKnappGeometrier] = useState(false);
  const {
    geometri,
    setGeometri,
    setIsRitverktygAktiv,
    ritverktyg,
    setRitverktyg,
    buffertRadie,
    setIsMarkeraAktiv,
  } = usePremiumContext();
  const { visaFelmeddelande, setVisaFelmeddelande, setFelmeddelande } =
    useKartstodContext();
  const {
    listaBeteckningsObjekt,
    setListaBeteckningsObjekt,
    klickFranKarta,
    setKlickFranKarta,
  } = useDataContext();
  const { wmsLayer, wmsSource, projection, mapView } = useMapSettingsContext();

  const [centerState, setCenterState] = usePersistedState<KartaLocalStorage>(
    mapStateStorageKey,
    {
      isMapOpen: props.getVisaKartStod,
      isMenuOpen: isBeteckningarOpen,
      properties: {
        center: {
          e: 609924,
          n: 6877630,
        },
        zoom: 2,
      },
    }
  );

  useEffect(() => {
    map.setTarget(mapRef.current as HTMLDivElement);
    map.setView(mapView);
    map.setLayerGroup(
      new LayerGroup({
        layers: [
          wmsLayer,
          koptVectorArea,
          hovradVectorArea,
          markeradVectorArea,
          ritverktygVectorArea,
          buffertArea,
          vectorMarker,
        ],
      })
    );
  }, []);

  useEffect(() => {
    const features = json.map((jsn: any) => new GeoJSON().readFeatures(jsn));
    setBeteckningar(features.flat());
  }, [json]);

  useEffect(() => {
    vectorSourceKopt.clear();
    vectorSourceKopt.addFeatures(beteckningar);
  }, [beteckningar]);

  useEffect(() => {
    vectorSourceHovrad.clear();
    const features = hovradBeteckning.map((jsn: any) =>
      new GeoJSON().readFeatures(jsn)
    );
    vectorSourceHovrad.addFeatures(features.flat());
  }, [hovradBeteckning]);

  useEffect(() => {
    vectorSourceMarkerad.clear();
    const features = markeradeBeteckningar.map((jsn: any) =>
      new GeoJSON().readFeatures(jsn)
    );
    vectorSourceMarkerad.addFeatures(features.flat());
  }, [markeradeBeteckningar]);

  useEffect(() => {
    vectorSourceHovrad.clear();
    const features = klickadBeteckning.map((jsn: any) =>
      new GeoJSON().readFeatures(jsn)
    );
    vectorSourceHovrad.addFeatures(features.flat());

    const featuresArr: Feature<Geometry>[] = vectorSourceHovrad.getFeatures();
    let currentExtent: Extent | undefined;

    for (let index = 0; index < featuresArr.length; ++index) {
      const extent = featuresArr[index].getGeometry()?.getExtent();
      if (index < 1) {
        currentExtent = extent;
      } else {
        if (currentExtent && extent) {
          currentExtent = extend(currentExtent, extent);
        }
      }
    }

    if (currentExtent) {
      map.getView().fit(currentExtent, {
        padding: [90, 270, 20, 20],
        maxZoom: 11,
        duration: 150,
      });
    }
  }, [klickadBeteckning]);

  const sokMedKoordinater = (n: number, e: number) => {
    setVisaKopKnapp(true);
    setVisaKopKnappGeometrier(false);
    setGeometri('pin');

    setIsMarkeraAktiv(false);
    setRitverktyg({
      isPin: false,
      isCirkel: false,
      isLinje: false,
      isPolygon: false,
    });

    setZoomLevel(false);
    vectorSourceMarker.clear();
    vectorSourceRitVerktyg.clear();

    const iconFeature = new Feature(new Point([e, n]));
    setIconMarker(iconFeature);
    vectorSourceMarker.addFeature(iconFeature);

    if (vectorSourceMarker.getFeatures()[0]) {
      const geometry: Extent | undefined = vectorSourceMarker
        .getFeatures()[0]
        .getGeometry()
        ?.getExtent();

      if (geometry !== undefined) {
        map.getView().fit(geometry, {
          padding: [20, 270, 20, 20],
          maxZoom: 11,
          duration: 500,
        });
      }
    }
  };

  useEffect(() => {
    if (checkLocalStorageFirstRun.current) {
      const mapStateString = localStorage.getItem(mapStateStorageKey);
      if (mapStateString !== null) {
        const mapState = JSON.parse(mapStateString);
        if (mapState.isMapOpen) {
          props.setVisaKartStod(true);
        }
        if (mapState.isMenuOpen) {
          setIsBeteckningarOpen(true);
        }
        if (
          mapState.properties.center !== null &&
          mapState.properties.zoom !== null
        ) {
          map.setView(
            new View({
              projection,
              center: [
                mapState.properties.center.e,
                mapState.properties.center.n,
              ],
              zoom: mapState.properties.zoom,
              maxZoom: mapView.getMaxZoom(),
            })
          );
        }
      }
      checkLocalStorageFirstRun.current = false;
      return;
    }
    const zoom = map.getView().getZoom();
    const center = map.getView().getCenter();
    if (zoom !== undefined && center !== undefined) {
      setCenterState({
        isMapOpen: props.getVisaKartStod,
        isMenuOpen: isBeteckningarOpen,
        properties: {
          center: {
            e: center[0],
            n: center[1],
          },
          zoom: zoom,
        },
      });
    }
  }, [props.getVisaKartStod, isBeteckningarOpen]);

  const koptVectorArea = new VectorLayer({
    source: vectorSourceKopt,
    zIndex: 1,
    style: new Style({
      fill: new Fill({
        color: 'rgba(0,0,0,0.4)',
      }),
      stroke: new Stroke({
        color: 'rgb(0,0,0)',
        width: 1,
      }),
    }),
  });

  const hovradVectorArea = new VectorLayer({
    source: vectorSourceHovrad,
    zIndex: 2,
    style: new Style({
      fill: new Fill({
        color: 'rgba(0,68,255, 0.7)',
      }),
      stroke: new Stroke({
        color: 'rgba(0,68,255, 0.7)',
        width: 1,
      }),
    }),
  });

  const markeradVectorArea = new VectorLayer({
    source: vectorSourceMarkerad,
    zIndex: 2,
    style: new Style({
      stroke: new Stroke({
        color: 'rgb(255,255,255, 1)',
        lineDashOffset: 30,
        lineDash: [15, 15],
        width: 3,
      }),
    }),
  });

  const ritverktygVectorArea = new VectorLayer({
    source: vectorSourceRitVerktyg,
    zIndex: 3,
    style: new Style({
      fill: new Fill({
        color: 'rgba(255,255,255, 0.4)',
      }),
      stroke: new Stroke({
        color: 'rgba(255,255,255, 1)',
        lineDashOffset: 4,
        lineDash: [10, 15],
        width: 2,
      }),
    }),
  });

  const buffertArea = new VectorLayer({
    source: vectorSourceBuffertZon,
    zIndex: 2,
    style: new Style({
      fill: new Fill({
        color: 'rgba(0,68,255, 0.6)',
      }),
    }),
  });

  const vectorMarker = new VectorLayer({
    source: vectorSourceMarker,
    zIndex: 4,
    style: new Style({
      image: new Icon({
        anchor: [0.5, 0.96],
        imgSize: [30, 30],
        src: marker,
      }),
    }),
  });

  const pinDraw = new Draw({
    source: vectorSourceRitVerktyg,
    type: GeometryType.POINT,
    style: new Style({
      stroke: new Stroke({
        color: 'rgb(0,0,0, 0.8)',
        lineDashOffset: 4,
        lineDash: [10, 15],
        width: 2,
      }),
      image: new Circle({
        radius: 5,
        stroke: new Stroke({
          color: 'rgb(0,0,0)',
          width: 1,
        }),
      }),
    }),
  });

  const cirkelDraw = new Draw({
    source: vectorSourceRitVerktyg,
    type: GeometryType.CIRCLE,
    style: new Style({
      fill: new Fill({
        color: 'rgba(255,255,255,0.3)',
      }),
      stroke: new Stroke({
        color: 'rgb(0,0,0, 0.8)',
        lineDashOffset: 4,
        lineDash: [10, 15],
        width: 2,
      }),
      image: new Circle({
        radius: 5,
        stroke: new Stroke({
          color: 'rgb(0,0,0)',
          width: 1,
        }),
      }),
    }),
  });

  const polygonDraw = new Draw({
    source: vectorSourceRitVerktyg,
    type: GeometryType.POLYGON,
    style: new Style({
      fill: new Fill({
        color: 'rgba(255,255,255,0.3)',
      }),
      stroke: new Stroke({
        color: 'rgb(0,0,0, 0.8)',
        lineDashOffset: 4,
        lineDash: [10, 15],
        width: 2,
      }),
      image: new Circle({
        radius: 5,
        stroke: new Stroke({
          color: 'rgb(0,0,0)',
          width: 1,
        }),
      }),
    }),
  });

  const lineDraw = new Draw({
    source: vectorSourceRitVerktyg,
    type: GeometryType.LINE_STRING,
    style: new Style({
      stroke: new Stroke({
        color: 'rgb(0,0,0, 0.8)',
        lineDashOffset: 4,
        lineDash: [10, 15],
        width: 2,
      }),
      image: new Circle({
        radius: 5,
        stroke: new Stroke({
          color: 'rgb(0,0,0)',
          width: 1,
        }),
      }),
    }),
  });

  const skapaBuffertZon = (
    geometry: Geometry | undefined,
    buffertRadie: string
  ) => {
    if (parseInt(buffertRadie) > 0) {
      if (geometry !== undefined) {
        const parser = new OL3Parser();
        parser.inject(Point, LineString, LinearRing, Polygon);

        const jstsGeometri = parser.read(geometry);
        const bufferOp = new BufferOp(jstsGeometri);
        const bufferedGeometri = bufferOp.getResultGeometry(buffertRadie);

        const feature = new Feature();
        feature.setGeometry(parser.write(bufferedGeometri));
        vectorSourceBuffertZon.addFeature(feature);
      }
    }
  };

  const varnaOmAntalFeaturesOverstiger500 = (featureCollection: any) => {
    const featuresNotNull = featureCollection.features.filter(
      (feature: any) => feature.properties['objekt_id'] !== null
    );

    if (featuresNotNull.length > 497) {
      setVisaFelmeddelande(true);
      setFelmeddelande(
        'Du har markerat en yta med fler än 500 fastigheter. ' +
          'Alla fastigheter har därför inte hämtats. Rita en ny markering för att komplettera din sökning.'
      );
    }
  };

  const checkDubbletter = (js: any) => {
    //bryt ut denna funktion?
    const hamtadeFeatures = json.map((feature: any) => {
      if (feature.type === 'Feature') {
        return feature.properties['fastighet'];
      }
      return false;
    });

    const unikaFeaturesFastigheter = js.filter(
      (feature: any) =>
        !hamtadeFeatures.includes(feature.properties['fastighet'])
    );

    if (
      unikaFeaturesFastigheter === undefined ||
      unikaFeaturesFastigheter.length === 0
    ) {
      setVisaFelmeddelande(true);
      setFelmeddelande('Du har redan köpt denna beteckning.');
    } else {
      setJson((jsondata) => [unikaFeaturesFastigheter, ...jsondata].flat());
      mappaTillBeteckningslistObjekt(unikaFeaturesFastigheter);
      setIsBeteckningarOpen(true);
    }
  };

  const mappaTillBeteckningslistObjekt = (unikaFeatursCollections) => {
    const nyData = unikaFeatursCollections.map((feature: any) => ({
      json: feature,
      markerad: false,
    }));
    setListaBeteckningsObjekt([...nyData, ...listaBeteckningsObjekt]);
  };

  const checkZoomLevel = (zoomLevel: number | undefined = 0) =>
    zoomLevel > 9.53;

  useEffect(() => {
    if (geometri === 'cirkel') {
      map.getInteractions().pop();
      setVisaKopKnapp(false);
      setVisaKopKnappGeometrier(false);
      vectorSourceMarker.clear();
      map.addInteraction(cirkelDraw);

      cirkelDraw.on('drawstart', (evt) => {
        setVisaKopKnappGeometrier(false);
        vectorSourceRitVerktyg.clear();
        vectorSourceBuffertZon.clear();
      });

      cirkelDraw.on('drawend', (evt) => {
        const korrektZoomNiva = checkZoomLevel(map.getView().getZoom());

        if (korrektZoomNiva) {
          const geometri: Geometry | undefined = evt.feature.getGeometry();
          const cirkel = geometri as CircleOL;
          const cirkelTillPolygon = fromCircle(cirkel);

          skapaBuffertZon(cirkelTillPolygon, buffertRadie);

          const radie = cirkel.getRadius();
          const center = cirkel.getCenter();
          const eKoord = Math.round(center[0]);
          const nKoord = Math.round(center[1]);

          const cirkelURL = URLUtils.skapaCirkelURL(
            eKoord,
            nKoord,
            radie,
            buffertRadie
          );

          setVisaKopKnappGeometrier(true);
          setFetchBeteckningarURL(cirkelURL);

          const drawCoords = (evt.target as any).sketchCoords_;
          const lastDrawCoords = drawCoords[drawCoords.length - 1];

          const kopKnapp = new Overlay({
            position: [lastDrawCoords[0], lastDrawCoords[1]],
            positioning: OverlayPositioning.CENTER_CENTER,
            element: document.getElementById(
              'kopKnappGeometrier'
            ) as HTMLDivElement,
            stopEvent: true,
          });

          map.addOverlay(kopKnapp);
        } else {
          setVisaFelmeddelande(true);
          setFelmeddelande(
            'Ospecifik markering. Testa att zooma in ytterligare.'
          );
        }
      });
    } else if (geometri === 'polygon') {
      map.getInteractions().pop();
      setVisaKopKnapp(false);
      setVisaKopKnappGeometrier(false);
      vectorSourceMarker.clear();
      map.addInteraction(polygonDraw);

      polygonDraw.on('drawstart', (evt) => {
        setVisaKopKnappGeometrier(false);
        vectorSourceRitVerktyg.clear();
        vectorSourceBuffertZon.clear();
      });

      polygonDraw.on('drawend', (evt) => {
        const korrektZoomNiva = checkZoomLevel(map.getView().getZoom());

        if (korrektZoomNiva) {
          skapaBuffertZon(evt.feature.getGeometry(), buffertRadie);
          const polygonKoordinater =
            evt.feature.getProperties().geometry.flatCoordinates;
          const polygonURL = URLUtils.skapaPolygonURL(
            buffertRadie,
            polygonKoordinater
          );

          setVisaKopKnappGeometrier(true);
          setFetchBeteckningarURL(polygonURL);

          const drawCoords = (evt.target as any).sketchCoords_[0];
          const firstDrawCoords = drawCoords[0];

          const kopKnapp = new Overlay({
            position: [firstDrawCoords[0], firstDrawCoords[1]],
            positioning: OverlayPositioning.CENTER_CENTER,
            element: document.getElementById(
              'kopKnappGeometrier'
            ) as HTMLDivElement,
            stopEvent: true,
          });

          map.addOverlay(kopKnapp);
        } else {
          setVisaFelmeddelande(true);
          setFelmeddelande(
            'Ospecifik markering. Testa att zooma in ytterligare.'
          );
        }
      });
    } else if (geometri === 'linje') {
      map.getInteractions().pop();
      setVisaKopKnapp(false);
      setVisaKopKnappGeometrier(false);
      vectorSourceMarker.clear();
      map.addInteraction(lineDraw);

      lineDraw.on('drawstart', (evt) => {
        setVisaKopKnappGeometrier(false);
        vectorSourceRitVerktyg.clear();
        vectorSourceBuffertZon.clear();
      });

      lineDraw.on('drawend', (evt) => {
        const korrektZoomNiva = checkZoomLevel(map.getView().getZoom());

        if (korrektZoomNiva) {
          skapaBuffertZon(evt.feature.getGeometry(), buffertRadie);
          const linjeKoordinater =
            evt.feature.getProperties().geometry.flatCoordinates;
          const linjeURL = URLUtils.skapaLinjeURL(
            buffertRadie,
            linjeKoordinater
          );

          setVisaKopKnappGeometrier(true);
          setFetchBeteckningarURL(linjeURL);

          const drawCoords = (evt.target as any).sketchCoords_;
          const lastDrawCoords = drawCoords[drawCoords.length - 1];

          const kopKnapp = new Overlay({
            position: [lastDrawCoords[0], lastDrawCoords[1]],
            positioning: OverlayPositioning.CENTER_CENTER,
            element: document.getElementById(
              'kopKnappGeometrier'
            ) as HTMLDivElement,
            stopEvent: true,
          });

          map.addOverlay(kopKnapp);
        } else {
          setVisaFelmeddelande(true);
          setFelmeddelande(
            'Ospecifik markering. Testa att zooma in ytterligare.'
          );
        }
      });
    } else if (geometri === 'pin') {
      setVisaKopKnappGeometrier(false);
      setVisaKopKnappGeometrier(false);
      vectorSourceRitVerktyg.clear();
      vectorSourceBuffertZon.clear();
      vectorSourceHovrad.clear();
      map.getInteractions().pop();
      map.addInteraction(pinDraw);
    } else {
      map.getInteractions().getArray()[8].setActive(false);
      setVisaKopKnapp(false);
      setVisaKopKnappGeometrier(false);
      vectorSourceMarker.clear();
      vectorSourceHovrad.clear();
      vectorSourceRitVerktyg.clear();
      vectorSourceBuffertZon.clear();
      setGeometri('');
    }
  }, [geometri, buffertRadie, visaFelmeddelande, json]);

  useEffect(() => {
    map.on('pointerdrag', (evt) => {
      map.getViewport().style.cursor = 'all-scroll';
    });
    map.on('pointerup' as any, (evt) => {
      map.getViewport().style.cursor = 'auto';
    });
  }, []);

  useEffect(() => {
    map.on('moveend', (evt) => {
      const zoom = evt.map.getView().getZoom();
      const center = evt.map.getView().getCenter();

      checkZoomLevel(zoom) ? setZoomLevel(false) : setZoomLevel(true);

      if (center !== undefined && zoom !== undefined) {
        setCenterState({
          isMapOpen: props.getVisaKartStod,
          isMenuOpen: isBeteckningarOpen,
          properties: {
            center: {
              e: center[0],
              n: center[1],
            },
            zoom: zoom,
          },
        });
      }
    });
  }, [props.getVisaKartStod, isBeteckningarOpen]);

  useEffect(() => {
    map.on('singleclick', (evt) => {
      setKlickFranKarta('');

      setKoordinater({
        nKoordinat: evt.coordinate[1],
        eKoordinat: evt.coordinate[0],
      });
      const isRitverktyg = evt.map.getInteractions().getArray()[8].getActive();

      if (isRitverktyg === false) {
        map.forEachFeatureAtPixel(evt.pixel, (featureLike) => {
          setKlickFranKarta(featureLike.getProperties().objekt_id);

          const executeScroll = () => {
            const fastighet = document.getElementById(
              featureLike.getProperties().objekt_id
            );

            if (fastighet !== null) {
              fastighet.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
              });
            }
          };

          executeScroll();
        });
      }
    });
  }, []);

  useEffect(() => {
    map.on('pointermove', (evt) => {
      const valtRitverktyg = evt.map
        .getInteractions()
        .getArray()[8]
        .getActive();
      if (valtRitverktyg === false) {
        let selected = true;
        if (selected) {
          selected = false;
          vectorSourceHovrad.clear();
        }

        map.forEachFeatureAtPixel(evt.pixel, (f) => {
          selected = true;

          const props = f.getProperties();
          const objektID = props.objekt_id;

          if (objektID !== undefined) {
            const featureCollection = json
              .filter((json: any) =>
                FeatureUtils.getBeteckningMedObjektID(json, objektID)
              )
              .flatMap((json: any) => new GeoJSON().readFeatures(json));

            vectorSourceHovrad.addFeatures(featureCollection);
          }
        });
      }
    });
  }, [json]);

  useEffect(() => {
    if (geometri === 'pin') {
      vectorSourceMarker.clear();

      const url = wmsSource.getFeatureInfoUrl(
        [koordinater.eKoordinat, koordinater.nKoordinat],
        0.1,
        projection,
        {
          INFO_FORMAT: 'application/json',
          QUERY_LAYERS:
            'Fastighetsytor_WFS,metria:rattighet_ga_yta,metria:rattighet_ga_idpunkt,metria:rattighet_ga_linje,metria:rattighet_ga_punkt',
          LAYERS:
            'Fastighetsytor_WFS,metria:rattighet_ga_yta,metria:rattighet_ga_idpunkt,metria:rattighet_ga_linje,metria:rattighet_ga_punkt',
          FEATURE_COUNT: '50',
        }
      );

      checkZoomLevel(map.getView().getZoom())
        ? setZoomLevel(false)
        : setZoomLevel(true);

      setFetchBeteckningarURL(url as string);
      setVisaKopKnapp(true);

      const kopKnapp = new Overlay({
        position: [koordinater.eKoordinat, koordinater.nKoordinat],
        positioning: OverlayPositioning.CENTER_CENTER,
        element: document.getElementById('kopKnapp') as HTMLDivElement,
        stopEvent: true,
      });

      map.addOverlay(kopKnapp);
      const iconFeature = new Feature(
        new Point([koordinater.eKoordinat, koordinater.nKoordinat])
      );
      setIconMarker(iconFeature);
      vectorSourceMarker.addFeature(iconFeature);
    }
  }, [koordinater]);

  const ref = useRef(true);

  useEffect(() => {
    //TODO - ändra variabelnamn
    if (ref.current) {
      ref.current = false;
      return;
    }

    if (geometri === '') {
      const nyListaBeteckningsObjekt = listaBeteckningsObjekt.map((obj) =>
        FeatureUtils.togglaEnMarkeringFranObjektListaMedObjektId(
          obj,
          klickFranKarta
        )
      );

      const nyMarkeradeBeteckningar = nyListaBeteckningsObjekt
        .filter((obj) => obj.markerad)
        .map((obj) => obj.json);

      setListaBeteckningsObjekt(nyListaBeteckningsObjekt);
      setMarkeradeBeteckningar(nyMarkeradeBeteckningar);
    }
  }, [klickFranKarta, koordinater]);

  const hamtaBeteckningarViaGeometri = () => {
    setVisaKopKnappGeometrier(false);

    fetch(fetchBeteckningarURL, {
      method: 'GET',
      headers: {
        Origin: '',
      },
    })
      .then((res) => res.json())
      .then((featureCollection) => {
        varnaOmAntalFeaturesOverstiger500(featureCollection);

        const featureArray =
          FeatureUtils.tvattaBortOtillatnaDetaljtyperIFeatureCollection(
            featureCollection
          );

        if (FeatureUtils.existsGAANLAGGNING(featureArray)) {
          const kommunnamn =
            FeatureUtils.getEventuelltKommunNanmnFranFeatureCollection(
              featureArray
            );
          FeatureUtils.addKommunnamnToGAAnlaggning(featureArray, kommunnamn);
        }

        const fastighetsytorAndRattigheter =
          FeatureUtils.removeDuplicatesOfRattigheter(featureArray);

        checkDubbletter(fastighetsytorAndRattigheter);
      })
      .catch((e) => console.log('Oväntat fel i underliggande system.' + e));
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ width: '100%', height: '500px' }} ref={mapRef}>
        <KoordinatSok
          sokMedKoordinater={sokMedKoordinater}
          koordinater={koordinater}
          setKoordinater={(k) => setKoordinater(k)}
        />
        <ModalFelmeddelande />
        {props.visaBeteckningar ? (
          <BeteckningComponent
            json={json}
            setJson={(js) => setJson(js)}
            beteckningar={beteckningar}
            setKlickadBeteckning={(b) => setKlickadBeteckning(b)}
            setHovradBeteckning={(b) => setHovradBeteckning(b)}
            markeradeBeteckningar={markeradeBeteckningar}
            setMarkeradeBeteckningar={(b) => setMarkeradeBeteckningar(b)}
            visaKopKnapp={visaKopKnapp}
            setVisaKopknapp={(b) => setVisaKopKnapp(b)}
            isBeteckningarOpen={isBeteckningarOpen}
            setIsBeteckningarOpen={(b) => setIsBeteckningarOpen(b)}
            zoomLevel={zoomLevel}
            fetchBeteckningarUrl={fetchBeteckningarURL}
            koordinater={koordinater}
            isPremium={props.isPremium}
          />
        ) : (
          <div></div>
        )}
        {props.visaRitVerktyg ? <PremiumContainer /> : null}
        <KopKnappGeometrier
          visaKopKnappGeometrier={visaKopKnappGeometrier}
          setVisaKopKnapp={(kopKnappGeometrier) =>
            setVisaKopKnappGeometrier(kopKnappGeometrier)
          }
          hamtaBeteckning={hamtaBeteckningarViaGeometri}
        />
      </div>
    </div>
  );
};

export default InitMap;
